import type { BillWithVotes, StageEntry } from '../../models'
import { bcnLawUrl, dateRangeLabel } from '../../helpers'
import type { VoteEntry } from './grouping'

export interface Phase {
  anchorId: string
  chamber: string
  role: string
  dateRange: string
  changeSummary: string
  lawUrl: string | null
  votes: VoteEntry[]
  icon: 'pending' | 'approved' | 'rejected'
}

const PHASE_KEYWORDS = ['Primer', 'Segundo', 'Tercer'] as const

export function buildPhases(bill: BillWithVotes): Phase[] {
  const phases: Phase[] = []

  for (const keyword of PHASE_KEYWORDS) {
    const laneStages: StageEntry[] = bill.stages.filter((s) =>
      s.phase.includes(keyword),
    )
    if (laneStages.length === 0) continue

    const chamber = laneStages[0]?.chamber ?? ''

    let role: string
    if (keyword === 'Primer') {
      role =
        bill.origin_chamber === chamber ? 'Cámara de Origen' : 'Cámara Revisora'
    } else if (keyword === 'Segundo') {
      role = 'Cámara Revisora'
    } else {
      role = 'Tercer Trámite'
    }

    const dateRange = dateRangeLabel(laneStages)

    // Walk stages in reverse, find the last "Discusión" stage (excluding urgencia) and extract summary
    let changeSummary = ''
    for (let i = laneStages.length - 1; i >= 0; i--) {
      const stage = laneStages[i]!
      const desc = stage.description
      if (desc.includes('Discusión') && !desc.includes('urgencia')) {
        if (desc.includes('Aprobado')) {
          if (desc.includes('modificaciones')) {
            changeSummary = 'Aprobado con modificaciones'
          } else if (desc.includes('general y') && desc.includes('particular')) {
            changeSummary = 'Aprobado en general y en particular'
          } else if (desc.includes('general')) {
            changeSummary = 'Aprobado en general'
          } else if (desc.includes('particular')) {
            changeSummary = 'Aprobado en particular'
          } else {
            changeSummary = 'Aprobado'
          }
        } else if (desc.includes('Rechazado')) {
          changeSummary = 'Rechazado'
        }
        if (changeSummary !== '') break
      }
    }

    const votes: VoteEntry[] = []
    for (const stage of laneStages) {
      if (stage.vote_index == null) continue
      const vote = bill.vote_sessions[stage.vote_index]
      if (vote == null) continue

      let typeLabel: string
      if (vote.vote_scope === 'amendment') {
        typeLabel = 'Particular · Norma específica'
      } else if (stage.description.includes('general')) {
        typeLabel = 'General'
      } else if (stage.description.includes('particular')) {
        typeLabel = 'Particular'
      } else if (stage.description.includes('única')) {
        typeLabel = 'Única'
      } else {
        typeLabel = vote.vote_type
      }

      const slug = vote.vote_slug
      if (!votes.some(([s]) => s === slug)) {
        votes.push([slug, vote, typeLabel])
      }
    }

    // Determine icon based on outcome votes (bill-scoped votes preferred)
    const outcomeCandidates: VoteEntry[] = votes.some(
      ([, v]) => v.vote_scope === 'bill',
    )
      ? votes.filter(([, v]) => v.vote_scope === 'bill')
      : votes

    let icon: Phase['icon']
    if (votes.length === 0) {
      icon = 'pending'
    } else if (outcomeCandidates.every(([, v]) => v.outcome === 'approved')) {
      icon = 'approved'
    } else {
      const last = outcomeCandidates[outcomeCandidates.length - 1]
      icon = last != null && last[1].outcome === 'approved' ? 'approved' : 'rejected'
    }

    let anchorId: string
    if (keyword === 'Primer') anchorId = 'lifecycle-step-primer'
    else if (keyword === 'Segundo') anchorId = 'lifecycle-step-segundo'
    else anchorId = 'lifecycle-step-tercer'

    phases.push({
      anchorId,
      chamber: chamber.toUpperCase(),
      role,
      dateRange,
      changeSummary,
      lawUrl: null,
      votes,
      icon,
    })
  }

  // Executive phase
  const hasOficio = bill.stages.some((s) =>
    s.description.includes('Oficio de ley al Ejecutivo'),
  )
  const hasPublication = bill.stages.some((s) =>
    s.description.includes('Diario Oficial'),
  )
  const hasLawNumber =
    bill.law_number != null && bill.law_number !== ''
  const isPublished = hasPublication || hasLawNumber

  if (hasOficio || isPublished) {
    let label: string
    if (isPublished) {
      label =
        hasLawNumber && bill.law_number !== ''
          ? `Publicado como ${bill.law_number}`
          : 'Publicado'
    } else {
      label = 'Promulgación pendiente'
    }
    const icon: Phase['icon'] = isPublished ? 'approved' : 'pending'
    const lawUrl =
      hasLawNumber && bill.law_number != null
        ? bcnLawUrl(bill.law_number)
        : null

    phases.push({
      anchorId: 'lifecycle-step-ejecutivo',
      chamber: 'PODER EJECUTIVO',
      role: '',
      dateRange: '',
      changeSummary: label,
      lawUrl,
      votes: [],
      icon,
    })
  }

  return phases
}
